import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useValidateRoomCode, useJoinStudyGroup } from '../lib/api';
import { useAuth } from '../lib/auth';

export function StudyGroupJoinPage() {
  const { roomCode } = useParams<{ roomCode: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [joinError, setJoinError] = useState('');
  const [autoJoining, setAutoJoining] = useState(false);

  const { data, isLoading, error: fetchError } = useValidateRoomCode(
    user ? roomCode! : null,
  );
  const joinMutation = useJoinStudyGroup();

  const preview = data?.preview;

  // Auto-join when preview loads (user came from share link)
  useEffect(() => {
    if (!preview || autoJoining || joinMutation.isPending) return;
    setAutoJoining(true);
    joinMutation.mutateAsync(roomCode!)
      .then((result) => {
        navigate(`/study-groups/${result.group.id}/lobby`, { replace: true });
      })
      .catch((err: any) => {
        setJoinError(err.message || 'Failed to join. Please try again.');
        setAutoJoining(false);
      });
  }, [preview]);

  const handleJoin = async () => {
    setJoinError('');
    try {
      const result = await joinMutation.mutateAsync(roomCode!);
      navigate(`/study-groups/${result.group.id}/lobby`, { replace: true });
    } catch (err: any) {
      setJoinError(err.message || 'Failed to join. Please try again.');
    }
  };

  // Loading states
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-slate-500">
        Checking authentication...
      </div>
    );
  }

  if (!user) return null; // Will redirect

  if (isLoading || (autoJoining && !joinError)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-slate-500">
        {autoJoining ? 'Joining study group...' : 'Loading lobby...'}
      </div>
    );
  }

  // Error states
  if (fetchError) {
    const errMsg = (fetchError as Error)?.message ?? '';

    let title = 'Could not find lobby';
    let description = 'This room code may be invalid. Double check and try again.';

    if (errMsg.includes('not found') || errMsg.includes('No study group')) {
      title = 'Lobby not found';
      description = 'This study group does not exist or has been removed.';
    } else if (errMsg.includes('full')) {
      title = 'Lobby is full';
      description = 'This study group has reached the maximum number of players.';
    } else if (errMsg.includes('already started')) {
      title = 'Session already started';
      description = 'This study group has already begun. You can no longer join.';
    }

    return (
      <div className="max-w-lg mx-auto pb-24 px-4">
        <div className="text-center py-16 space-y-4">
          <div className="text-4xl">🔍</div>
          <h2 className="text-lg font-bold text-squishy-dark">{title}</h2>
          <p className="text-sm text-slate-500 max-w-xs mx-auto">{description}</p>
          <button
            onClick={() => navigate('/study-groups')}
            className="bg-squishy-primary text-white font-medium px-6 py-2.5 rounded-xl text-sm hover:bg-teal-600 transition-colors"
          >
            Go to Study Groups
          </button>
        </div>
      </div>
    );
  }

  if (!preview) return null;

  return (
    <div className="max-w-lg mx-auto pb-24 px-4 space-y-5">
      {/* ── Header ────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/study-groups')}
          className="text-slate-500 hover:text-squishy-dark transition-colors"
          aria-label="Back"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-xl font-bold text-squishy-dark">Join Study Group</h1>
      </div>

      {/* ── Lobby preview card ────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        {/* Title */}
        <div className="text-center">
          <div className="text-lg font-bold text-squishy-dark">{preview.title}</div>
          <div className="text-xs text-slate-500 mt-1">{preview.sourceName}</div>
        </div>

        {/* Details */}
        <div className="space-y-2 border-t border-slate-100 pt-3">
          <DetailRow label="Questions" value={String(preview.questionCount)} />
          <DetailRow label="Timer" value={`${preview.timerSeconds} seconds`} />
          <DetailRow label="Players" value={`${preview.memberCount}/${preview.memberCap}`} />
        </div>
      </div>

      {/* ── Join button ───────────────────────────────────────── */}
      {joinError && (
        <p className="text-xs text-red-500">{joinError}</p>
      )}

      <button
        onClick={handleJoin}
        disabled={joinMutation.isPending}
        className="w-full bg-squishy-primary hover:bg-teal-600 text-white font-semibold py-3 rounded-xl disabled:opacity-50 transition-all"
      >
        {joinMutation.isPending ? 'Joining...' : 'Join Group'}
      </button>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-800">{value}</span>
    </div>
  );
}
